import DOMPurify from "dompurify";

const PURIFY_CONFIG: DOMPurify.Config = {
  // Allow SVG elements and attributes
  ALLOWED_TAGS: [
    "svg",
    "g",
    "path",
    "circle",
    "rect",
    "line",
    "polyline",
    "polygon",
    "ellipse",
    "text",
    "tspan",
    "defs",
    "linearGradient",
    "radialGradient",
    "stop",
    "mask",
    "clipPath",
    "pattern",
    "image",
    "use",
    "symbol",
  ],
  ALLOWED_ATTR: [
    "viewBox",
    "width",
    "height",
    "x",
    "y",
    "cx",
    "cy",
    "r",
    "rx",
    "ry",
    "d",
    "points",
    "fill",
    "stroke",
    "stroke-width",
    "stroke-linecap",
    "stroke-linejoin",
    "stroke-dasharray",
    "opacity",
    "transform",
    "id",
    "class",
    "style",
    "x1",
    "y1",
    "x2",
    "y2",
    "href",
    "xlink:href",
    "gradientUnits",
    "gradientTransform",
    "offset",
    "stop-color",
    "stop-opacity",
    "maskUnits",
    "clipPathUnits",
    "patternUnits",
    "patternTransform",
    "font-family",
    "font-size",
    "font-weight",
    "text-anchor",
    "dominant-baseline",
    "xmlns",
    "xmlns:xlink",
  ],
  // Remove dangerous attributes
  FORBID_ATTR: [
    "onload",
    "onerror",
    "onclick",
    "onmouseover",
    "onmouseout",
    "onmousedown",
    "onmouseup",
    "onfocus",
    "onblur",
    "onchange",
    "onsubmit",
    "onreset",
    "onselect",
    "onkeydown",
    "onkeyup",
    "onkeypress",
    "onabort",
    "oncanplay",
    "oncanplaythrough",
    "ondurationchange",
    "onemptied",
    "onended",
    "onerror",
    "onloadeddata",
    "onloadedmetadata",
    "onloadstart",
    "onpause",
    "onplay",
    "onplaying",
    "onprogress",
    "onratechange",
    "onseeked",
    "onseeking",
    "onstalled",
    "onsuspend",
    "ontimeupdate",
    "onvolumechange",
    "onwaiting",
  ],
  FORBID_TAGS: ["script", "object", "embed", "iframe", "link", "meta"],
  ALLOWED_URI_REGEXP:
    /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  SANITIZE_DOM: true,
  KEEP_CONTENT: true,
  REMOVE_COMMENTS: true,
  REMOVE_EMPTY_ATTR: true,
};

const DANGEROUS_PATTERNS = [
  /javascript:/i,
  /data:text\/html/i,
  /vbscript:/i,
  /on\w+\s*=/i,
  /<script/i,
  /<iframe/i,
  /<object/i,
  /<embed/i,
  /<link/i,
  /<meta/i,
  /expression\s*\(/i,
  /url\s*\(/i,
  /@import/i,
  /behavior\s*:/i,
  /-moz-binding/i,
];

const HTML_PURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "s", "strike", "span", "p", "br", "ul", "ol", "li", "label", "input", "iframe"],
  ALLOWED_ATTR: [
    "style",
    "class",
    "type",
    "checked",
    "disabled",
    "data-type",
    "data-checked",
    "src",
    "width",
    "height",
    "frameborder",
    "allow",
    "allowfullscreen",
    "loading",
    "referrerpolicy",
    "title",
  ],
  FORBID_TAGS: ["script", "object", "embed", "link", "meta", "img", "form"],
  FORBID_ATTR: [
    "onload", "onerror", "onclick", "onmouseover", "onmouseout", "onmousedown", "onmouseup",
    "onfocus", "onblur", "onchange", "onsubmit", "onreset", "onselect", "onkeydown", "onkeyup",
    "onkeypress", "oninput",
  ],
  SANITIZE_DOM: true,
  KEEP_CONTENT: true,
  REMOVE_COMMENTS: true,
  FORCE_BODY: false,
};

export const sanitizeHotspotHTML = (html: string): string => {
  if (!html || typeof html !== "string") {
    return "";
  }
  return DOMPurify.sanitize(html, HTML_PURIFY_CONFIG);
};

export const sanitizeSvgMarkup = (svgMarkup: string): string => {
  if (!svgMarkup || typeof svgMarkup !== "string") {
    return "";
  }

  const trimmed = svgMarkup.trim();
  if (!trimmed) {
    return "";
  }

  const sanitized = DOMPurify.sanitize(trimmed, PURIFY_CONFIG);
  if (!sanitized || sanitized.trim() === "") {
    return "";
  }

  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(sanitized)) {
      return "";
    }
  }

  return sanitized.trim();
};
